import { useEffect } from 'react';
import {
    FormControl,
    FormHelperText,
    FormLabel,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Box,
    SxProps,
    Theme,
    Radio,
    Typography,
} from '@mui/material';
import { useField } from 'formik';
import isEqual from 'lodash/isEqual';

import InformationCircleIcon from 'modules/common/components/Icons/InformationCircleIcon';

interface Option {
    value: boolean | string | number;
    label: string;
    description?: string;
}

interface YesNoFieldProps {
    label?: string;
    helperText?: string;
    description?: string;
    name: string;
    buttonsStyle?: SxProps<Theme>;
    onChange?: (value: boolean | string | number) => void;
    tooltipText?: string;
    options?: Option[];
    sx?: SxProps<Theme>;
    disabled?: boolean;
    defaultValue?: any;
    /** When true, buttons are displayed side by side on all screen sizes including mobile */
    horizontal?: boolean;
}

const DEFAULT_OPTIONS: Option[] = [
    { value: true, label: 'Yes' },
    { value: false, label: 'No' },
];

function YesNoField({
    label = '',
    helperText,
    description,
    name,
    buttonsStyle,
    onChange,
    tooltipText = '',
    options = DEFAULT_OPTIONS,
    sx,
    disabled,
    defaultValue,
    horizontal = false,
}: YesNoFieldProps): JSX.Element {
    const [{ value }, { touched, error }, { setValue }] = useField(name);

    useEffect(() => {
        if (defaultValue !== undefined && defaultValue !== null) {
            setValue(defaultValue);
        }
    }, [defaultValue]);

    const isSelected = (optionValue: boolean | string | number): boolean => {
        if (typeof value === 'boolean' && typeof optionValue === 'boolean') {
            return value === optionValue;
        }
        return isEqual(value, optionValue);
    };

    return (
        <FormControl
            error={touched && !!error}
            variant="standard"
            fullWidth
            sx={{
                ...sx,
                '& .MuiToggleButtonGroup-root': {
                    textWrap: 'nowrap',
                    flexDirection: horizontal ? 'row' : { xs: 'column', sm: 'row' },
                    gap: 2,
                    '& .MuiToggleButtonGroup-grouped, & .MuiToggleButtonGroup-grouped.Mui-selected': {
                        paddingInline: 2,
                        paddingBlock: '15px',
                        flex: 1,
                        ':not(:first-of-type)': {
                            marginLeft: horizontal ? 0 : { xs: 0 },
                        },
                    },
                    '& .MuiToggleButtonGroup-grouped.Mui-selected': {
                        paddingBlock: '14px',
                    },
                },
            }}>
            {label && (
                <FormLabel sx={{ mb: 2 }}>
                    <>
                        {label}
                        {tooltipText && (
                            <Tooltip title={tooltipText}>
                                <Box
                                    component="span"
                                    sx={{
                                        verticalAlign: 'middle',
                                    }}>
                                    <InformationCircleIcon
                                        sx={{
                                            color: 'grey.700',
                                            ml: 1.5,
                                            fontSize: '1rem',
                                        }}
                                    />
                                </Box>
                            </Tooltip>
                        )}
                    </>
                    {description && (
                        <Typography variant="body1" color="text.secondary" mt={0.25}>
                            {description}
                        </Typography>
                    )}
                </FormLabel>
            )}

            <ToggleButtonGroup
                color={touched && !!error ? 'error' : 'info'}
                data-testid={name}
                variant="outlinedControl"
                value={value}
                disabled={disabled}
                exclusive>
                {options.map((option) => (
                    <ToggleButton
                        key={String(option.value)}
                        name={name}
                        value={option.value}
                        sx={{
                            ...buttonsStyle,
                        }}
                        selected={isSelected(option.value)}
                        onClick={async () => {
                            // Set the value and trigger validation
                            await setValue(option.value, true);
                            // Call the onChange callback if provided
                            onChange?.(option.value);
                        }}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: option.description ? 'flex-start' : 'center',
                                flex: 1,
                            }}>
                            <Radio
                                checked={isSelected(option.value)}
                                disabled={disabled}
                                disableRipple
                                size="medium"
                                sx={{ marginRight: 1, pointerEvents: 'none', padding: 0.75 }}
                            />
                            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                                <Typography variant="body1" component="span">
                                    {option.label}
                                </Typography>
                                {option.description && (
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        component="span"
                                        sx={{ textWrap: 'auto' }}>
                                        {option.description}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    </ToggleButton>
                ))}
            </ToggleButtonGroup>
            {touched && !!error && <FormHelperText>{error}</FormHelperText>}
            {helperText && (!error || !touched) && <FormHelperText>{helperText}</FormHelperText>}
        </FormControl>
    );
}

export default YesNoField;
